import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FilterIcon from '@mui/icons-material/FilterListOutlined';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import DistributorsTable from './DistributorsTable';
import DistributorsTableMobile from './DistributorsTableMobile';
import FilterDistributorsModal from './FilterDistributorsModal';
import DistributorsModal from './DistributorsModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchDistributors,
  updateDistributor,
  deleteDistributor,
  addDistributor,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './distributors.scss';

const DistributorsTableContainer = (props) => {
  const {
    distributors = [],
    createDistributor,
    updateDistributor,
    deleteDistributor,
    fetchDistributors,
    withButton,
    toggleSidebar,
    countries,
    procedures = [],
    companies = [],
    currentUser,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ companies: [], countries: [] });

  const onEditClick = (distributor) => {
    setSelectedDistributor(distributor);
    toggleUpdateModal(true);
  };

  const onDistributorCreate = async (formObj) => {
    try {
      setLoading(true);

      await createDistributor(formObj);
      await fetchDistributors();
      setSuccessMessage('New distributor successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onDistributorUpdate = async (formObj) => {
    if (selectedDistributor) {
      try {
        setLoading(true);

        await updateDistributor(selectedDistributor?.id, formObj);
        await fetchDistributors();
        setSuccessMessage('Distributor successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteDistributor = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteDistributor(selectedId);
        await fetchDistributors();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      companies: filters.companies || [],
      countries: filters.countries || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredDistributors = useMemo(() => {
    let filtered = distributors;

    if (search) {
      const q = search.toLowerCase();
      filtered = distributors.filter((p) => p?.name?.toLowerCase()?.includes(q));
    }

    if (filterState?.companies?.length) {
      filtered = filtered?.filter((d) => filterState?.companies?.map((c) => c.id)?.includes(d.company));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((d) => filterState?.countries?.includes(d.country));
    }

    return filtered;
  }, [distributors, search, filterState]);

  const list = useMemo(() => {
    let result = filteredDistributors;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredDistributors, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Distributors
                  <Box className="badge">{distributors.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add, view or manage distributors
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add Distributor'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {distributors.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{key === 'companies' ? value?.name : value}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }

              <DistributorsTable
                distributors={list}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredDistributors?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                companies={companies}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <DistributorsTableMobile distributors={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Distributors</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteDistributor}
        title="Delete Distributor"
        text="Are you sure you want to delete this distributor?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <DistributorsModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onDistributorCreate}
        countries={countries}
        loading={loading}
        proceduresList={procedures}
        companies={companies}
        currentUser={currentUser}
      />

      <DistributorsModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedDistributor(undefined);
        }}
        onSubmit={onDistributorUpdate}
        countries={countries}
        mode='update'
        initialValues={selectedDistributor}
        loading={loading}
        proceduresList={procedures}
        companies={companies}
        currentUser={currentUser}
      />

      <FilterDistributorsModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
        companies={companies}
        currentUser={currentUser}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteDistributor}
        title="Delete Distributor?"
        text="Are you sure you want to delete this distributor? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

DistributorsTableContainer.propTypes = {
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    procedures: state.procedures.list,
    currentUser: state.user.currentUser,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchDistributors: () => dispatch(fetchDistributors()),
    createDistributor: (distributorData) => dispatch(addDistributor(distributorData)),
    updateDistributor: (id, distributorData) => dispatch(updateDistributor(id, distributorData)),
    deleteDistributor: (id) => dispatch(deleteDistributor(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DistributorsTableContainer);
